// Types
import { MeasurementType } from '../../../../../types/global';
import { CrusherModel, EquipmentUnitDataConeCrusher } from '../../../../../types/crusher';

// Layout
import { SeriesColorEnum } from '../../../../../layout/elements/LineChart';

/* **************************************** ENUMS ********************************************** */

export enum SensorCategory {
  hydraulic = 'hydraulic',
  motor = 'motor',
  lube = 'lube',
  vibration = 'vibration',
  cavityLevel = 'cavityLevel',
}

/* *************************************** INTERFACES ******************************************* */

export interface SensorConfig {
  category: SensorCategory;
  decimals: number;
  enableFlag?: keyof EquipmentUnitDataConeCrusher;
  maxScale: number;
  measurementType?: MeasurementType;
  minScale: number;
  nameKey: string;
  requiredModel?: CrusherModel;
  unit: string;
}

/* **************************************** TYPES ********************************************** */

export type SensorTag =
  | 'hydraulicClampPressure'
  | 'hydraulicTrampPressure'
  | 'crusherMotorAmps'
  | 'crusherMotor2Amps'
  | 'crusherMotorRpm'
  | 'lubricationReturnTemperature'
  | 'lubricationTankTemperature'
  | 'lubricationSupplyTemperature'
  | 'lubricationOilPressure'
  | 'lubeFlowScld1'
  | 'lubeFlowScld2'
  | 'vibrationSensor1'
  | 'vibrationSensor2'
  | 'vibrationSensor3'
  | 'vibrationSensor4'
  | 'cavityLevel';

/* **************************************** CONSTANTS ******************************************* */

export const MAX_SELECTED_TAGS: number = 6;
export const MAX_Y_AXES: number = 4;

// Positional color palette: index 0 = 1st selected sensor, index 1 = 2nd, etc.
// Colors are visually distinct to guarantee no repetition among the 6 slots.
export const SERIES_COLOR_PALETTE: SeriesColorEnum[] = [
  SeriesColorEnum.blue,
  SeriesColorEnum.orange,
  SeriesColorEnum.pink,
  SeriesColorEnum.lightBlue,
  SeriesColorEnum.green,
  SeriesColorEnum.yellow,
];

export const SENSOR_CATEGORY_ORDER: SensorCategory[] = [
  SensorCategory.hydraulic,
  SensorCategory.motor,
  SensorCategory.lube,
  SensorCategory.vibration,
  SensorCategory.cavityLevel,
];

// Keyed by SensorTag for completeness, but exported as Record<string, ...>
// so consumers can index with dynamic strings from selectedTags.
export const SENSOR_CONFIG: { [K in SensorTag]: SensorConfig } & Record<string, SensorConfig> = {
  hydraulicClampPressure: {
    category: SensorCategory.hydraulic,
    decimals: 0,
    maxScale: 25,
    measurementType: MeasurementType.pressure,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.hydraulicClampPressure',
    unit: 'MPa',
  },
  hydraulicTrampPressure: {
    category: SensorCategory.hydraulic,
    decimals: 0,
    maxScale: 25,
    measurementType: MeasurementType.pressure,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.hydraulicTrampPressure',
    unit: 'MPa',
  },
  crusherMotorAmps: {
    category: SensorCategory.motor,
    decimals: 0,
    maxScale: 750,
    minScale: 0,
    nameKey: 'view.crusher.trendings.motorCurrent.title',
    unit: 'A',
  },
  crusherMotor2Amps: {
    category: SensorCategory.motor,
    decimals: 0,
    enableFlag: 'crusherMotorQty',
    maxScale: 750,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.crusherMotor2Amps',
    unit: 'A',
  },
  crusherMotorRpm: {
    category: SensorCategory.motor,
    decimals: 0,
    enableFlag: 'crusherSpeedMonitorEnable',
    maxScale: 500,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.crusherMotorRpm',
    unit: 'RPM',
  },
  lubricationReturnTemperature: {
    category: SensorCategory.lube,
    decimals: 0,
    maxScale: 60,
    measurementType: MeasurementType.temperature,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubricationReturnTemperature',
    unit: '°C',
  },
  lubricationTankTemperature: {
    category: SensorCategory.lube,
    decimals: 0,
    maxScale: 60,
    measurementType: MeasurementType.temperature,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubricationTankTemperature',
    unit: '°C',
  },
  lubricationSupplyTemperature: {
    category: SensorCategory.lube,
    decimals: 0,
    enableFlag: 'lubeSupplyTempEnable',
    maxScale: 60,
    measurementType: MeasurementType.temperature,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubricationSupplyTemperature',
    unit: '°C',
  },
  lubricationOilPressure: {
    category: SensorCategory.lube,
    decimals: 2,
    maxScale: 0.27579,
    measurementType: MeasurementType.pressure,
    minScale: 0,
    nameKey: 'view.crusher.trendings.lubeUnit.legend.lubePressure',
    unit: 'MPa',
  },
  lubeFlowScld1: {
    category: SensorCategory.lube,
    decimals: 0,
    enableFlag: 'lubeFlow1Enable',
    maxScale: 10,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubeFlowScld1',
    requiredModel: CrusherModel.p500,
    unit: 'L/min',
  },
  lubeFlowScld2: {
    category: SensorCategory.lube,
    decimals: 0,
    enableFlag: 'lubeFlow2Enable',
    maxScale: 10,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.lubeFlowScld2',
    requiredModel: CrusherModel.p500,
    unit: 'L/min',
  },
  vibrationSensor1: {
    category: SensorCategory.vibration,
    decimals: 0,
    enableFlag: 'vibrationSensor1Enable',
    maxScale: 100,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.vibrationSensor1',
    unit: '%',
  },
  vibrationSensor2: {
    category: SensorCategory.vibration,
    decimals: 0,
    enableFlag: 'vibrationSensor2Enable',
    maxScale: 100,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.vibrationSensor2',
    unit: '%',
  },
  vibrationSensor3: {
    category: SensorCategory.vibration,
    decimals: 0,
    enableFlag: 'vibrationSensor3Enable',
    maxScale: 100,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.vibrationSensor3',
    unit: '%',
  },
  vibrationSensor4: {
    category: SensorCategory.vibration,
    decimals: 0,
    enableFlag: 'vibrationSensor4Enable',
    maxScale: 100,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.vibrationSensor4',
    unit: '%',
  },
  cavityLevel: {
    category: SensorCategory.cavityLevel,
    decimals: 0,
    enableFlag: 'cavityLevelSensorEnable',
    maxScale: 100,
    minScale: 0,
    nameKey: 'view.crusher.trendings.custom.sensors.cavityLevel',
    unit: '%',
  },
};
